import React, { useState, useEffect } from 'react';
import { get } from '../../helpers/apiHelper';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import Swal from 'sweetalert2';
import {
    FaPlus, FaSearch,
    FaTag, FaChartLine, FaBullseye
} from 'react-icons/fa';
import OfertaCard from '../../components/ofertas/OfertaCard';
import CrearOfertaModal from '../../components/ofertas/CrearOfertaModal';
import OfertaDetailModal from '../../components/ofertas/OfertaDetailModal';
import { useOfertasUltraOptimized } from '../../hooks/useOfertasUltraOptimized';
import ofertasService from '../../helpers/ofertasService';
import OfertasOfflineMode from '../../components/ofertas/OfertasOfflineMode';

function OfertasPage() {
    const [marcas, setMarcas] = useState([]);
    const [filterType, setFilterType] = useState('all');
    const [filterEstado, setFilterEstado] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMarca, setSelectedMarca] = useState('all');

    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedOferta, setSelectedOferta] = useState(null);
    const [editingOferta, setEditingOferta] = useState(null);
    const { user } = useAuthStore();
    const userId = user.userId;

    // Usar el hook personalizado para ofertas
    const filters = {
        tipo: filterType !== 'all' ? filterType : undefined,
        estado: filterEstado !== 'all' ? filterEstado : undefined,
        marca: selectedMarca !== 'all' ? selectedMarca : undefined,
        search: searchTerm || undefined
    };

    const {
        ofertas,
        loading,
        error,
        createOferta,
        updateOferta,
        deleteOferta,
        changeEstado,
        refreshOfertas
    } = useOfertasUltraOptimized(filters);

    const fetchMarcas = React.useCallback(async () => {
        try {
            const response = await get(`marcas?usuario=${userId}`);
            setMarcas(response);
        } catch (error) {
            console.error("Error al obtener marcas:", error);
            Swal.fire("Error", error.message, "error");
        }
    }, [userId]);

    useEffect(() => {
        // Solo cargar marcas una vez al montar el componente
        if (userId && marcas.length === 0) {
            fetchMarcas();
        }
    }, [userId]); // Solo cuando cambia el userId

    const handleDelete = async (oferta) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deleteOferta(oferta._id);
                    Swal.fire('Eliminado', 'La oferta ha sido eliminada correctamente', 'success');
                } catch (error) {
                    console.error('Error al eliminar oferta:', error);
                    Swal.fire('Error', error.message, 'error');
                }
            }
        });
    };

    const handleToggleStatus = async (oferta, newStatus) => {
        try {
            await changeEstado(oferta._id, newStatus);
            Swal.fire('Actualizado', `Estado cambiado a ${newStatus}`, 'success');
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleViewDetails = (oferta) => {
        setSelectedOferta(oferta);
        setShowDetailModal(true);
    };

    const handleEdit = (oferta) => {
        setEditingOferta(oferta);
        setShowCreateModal(true);
    };

    const handleCreateSuccess = () => {
        refreshOfertas();
        setShowCreateModal(false);
        setEditingOferta(null);
    };



    // Los filtros ya se aplican en el hook useOfertas, no necesitamos filtrar aquí
    const filteredOfertas = ofertas;

    // Usar el servicio para obtener las opciones
    const ofertaTypes = [
        { value: 'all', label: 'Todos los tipos' },
        ...ofertasService.getTiposOferta()
    ];

    const estados = [
        { value: 'all', label: 'Todos los estados' },
        ...ofertasService.getEstados()
    ];

    if (loading && ofertas.length === 0) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando ofertas...</p>
                </div>
            </div>
        );
    }

    // Si hay error de red y no hay ofertas, mostrar modo offline
    if (error && ofertas.length === 0 && (error.includes('NetworkError') || error.includes('fetch'))) {
        return <OfertasOfflineMode onRetry={refreshOfertas} />;
    }

    if (error && ofertas.length === 0) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-6">
                    <div className="text-red-500 text-6xl mb-4">⚠️</div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">
                        Error al cargar las ofertas
                    </h2>
                    <p className="text-gray-600 mb-4">
                        {error}
                    </p>
                    <div className="space-y-2">
                        <button
                            onClick={refreshOfertas}
                            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Reintentar
                        </button>
                        <button
                            onClick={() => window.location.reload()}
                            className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            Recargar página
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">
                        Gestión de Ofertas
                    </h1>
                    <p className="text-gray-600">
                        Administra todas tus ofertas promocionales para aumentar ventas y fidelizar clientes
                    </p>
                </div>

                {/* Error Banner */}
                {error && ofertas.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="text-yellow-500 mr-3">⚠️</div>
                                <div>
                                    <h3 className="text-sm font-medium text-yellow-800">
                                        Advertencia de conexión
                                    </h3>
                                    <p className="text-sm text-yellow-700 mt-1">
                                        {error}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={refreshOfertas}
                                className="text-sm bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700 transition-colors"
                            >
                                Reintentar
                            </button>
                        </div>
                    </div>
                )}

                {/* Filtros y búsqueda */}
                <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                        {/* Búsqueda */}
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-3 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar ofertas..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Filtro por tipo */}
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            {ofertaTypes.map(type => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>

                        {/* Filtro por estado */}
                        <select
                            value={filterEstado}
                            onChange={(e) => setFilterEstado(e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            {estados.map(estado => (
                                <option key={estado.value} value={estado.value}>
                                    {estado.label}
                                </option>
                            ))}
                        </select>

                        {/* Filtro por marca */}
                        <select
                            value={selectedMarca}
                            onChange={(e) => setSelectedMarca(e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="all">Todas las marcas</option>
                            {marcas.map(marca => (
                                <option key={marca._id} value={marca._id}>
                                    {marca.nombre || marca.displayName}
                                </option>
                            ))}
                        </select>

                        {/* Botón crear nueva oferta */}
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center"
                        >
                            <FaPlus className="mr-2" />
                            Nueva Oferta
                        </button>
                    </div>
                </div>

                {/* Estadísticas rápidas */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaTag className="text-blue-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">Total Ofertas</p>
                                <p className="text-2xl font-bold text-gray-800">{ofertas.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaTag className="text-green-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">Ofertas Activas</p>
                                <p className="text-2xl font-bold text-gray-800">
                                    {ofertas.filter(o => o.estado === 'ACTIVA').length}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaChartLine className="text-purple-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">Total Usos</p>
                                <p className="text-2xl font-bold text-gray-800">
                                    {ofertas.reduce((total, oferta) => total + (oferta.usos || 0), 0)}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaBullseye className="text-orange-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">Total Visualizaciones</p>
                                <p className="text-2xl font-bold text-gray-800">
                                    {ofertas.reduce((total, oferta) => total + (oferta.visualizaciones || 0), 0)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vista de ofertas */}
                {filteredOfertas.length === 0 ? (
                    <div className="bg-white p-8 rounded-lg shadow-md text-center">
                        <FaTag className="text-4xl text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-600 mb-2">
                            No se encontraron ofertas
                        </h3>
                        <p className="text-gray-500 mb-4">
                            {searchTerm || filterType !== 'all' || filterEstado !== 'all' || selectedMarca !== 'all'
                                ? 'Intenta ajustar los filtros de búsqueda'
                                : 'Crea tu primera oferta para comenzar'
                            }
                        </p>
                        {!searchTerm && filterType === 'all' && filterEstado === 'all' && selectedMarca === 'all' && (
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                            >
                                Crear Primera Oferta
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredOfertas.map((oferta) => (
                            <OfertaCard
                                key={oferta._id}
                                oferta={oferta}
                                onView={handleViewDetails}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onToggleStatus={handleToggleStatus}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Modals */}
            <CrearOfertaModal
                isOpen={showCreateModal}
                onClose={() => {
                    setShowCreateModal(false);
                    setEditingOferta(null);
                }}
                oferta={editingOferta}
                onSuccess={handleCreateSuccess}
            />

            <OfertaDetailModal
                isOpen={showDetailModal}
                oferta={selectedOferta}
                onClose={() => {
                    setShowDetailModal(false);
                    setSelectedOferta(null);
                }}
                onDelete={handleDelete}
                onEdit={handleEdit}
            />
        </div>
    );
}

export default OfertasPage; 